import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import FileUpload from '../../components/common/FileUpload';
import { employeeAPI, utilityAPI } from '../../services/api';
import { toast } from 'sonner';
import { User, Camera, X, Loader2, Save } from 'lucide-react';

const Profile = ({ isFounderMode = false }) => {
  const [profile, setProfile] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [skillInput, setSkillInput] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, industriesRes] = await Promise.all([
        employeeAPI.getProfile(),
        utilityAPI.getIndustries()
      ]);
      setProfile(profileRes.data);
      setIndustries(industriesRes.data);
    } catch (error) {
      // Handle 403/404 as empty profile state (first time user)
      if (error.response?.status === 403 || error.response?.status === 404) {
        setProfile({});
        try {
          const industriesRes = await utilityAPI.getIndustries();
          setIndustries(industriesRes.data);
        } catch (e) {
          // Silently ignore industries load failure
        }
      } else {
        console.error('Error fetching profile:', error);
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleAddSkill = (e) => {
    e.preventDefault();
    if (skillInput.trim() && !profile.skills?.includes(skillInput.trim())) {
      setProfile(prev => ({
        ...prev,
        skills: [...(prev.skills || []), skillInput.trim()]
      }));
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skill) => {
    setProfile(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await employeeAPI.updateProfile(profile);
      toast.success('Profile saved successfully!');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    const content = (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
      </div>
    );
    return isFounderMode ? content : <DashboardLayout>{content}</DashboardLayout>;
  }

  const profileContent = (
    <div className="max-w-3xl space-y-8" data-testid="employee-profile">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="text-3xl font-bold text-[#0A0F1E]">Edit Profile</h1>
        <p className="mt-2 text-[#5B6472]">Update your professional information.</p>
      </div>

        {/* Profile Photo */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
          <Label className="text-[#0A0F1E] mb-4 block">Profile Photo</Label>
          <FileUpload
            value={profile?.photo_url || ''}
            onChange={(url) => handleChange('photo_url', url)}
            placeholder="Upload profile photo"
          />
        </Card>

        {/* Basic Info */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
          <h2 className="text-lg font-semibold text-[#0A0F1E] mb-6">Basic Information</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">First Name</Label>
              <Input
                value={profile?.first_name || ''}
                onChange={(e) => handleChange('first_name', e.target.value)}
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                data-testid="profile-first-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Last Name</Label>
              <Input
                value={profile?.last_name || ''}
                onChange={(e) => handleChange('last_name', e.target.value)}
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                data-testid="profile-last-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Job Title</Label>
              <Input
                value={profile?.job_title || ''}
                onChange={(e) => handleChange('job_title', e.target.value)}
                placeholder="e.g., Senior Product Manager"
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                data-testid="profile-job-title"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Industry</Label>
              <Select value={profile?.industry || ''} onValueChange={(v) => handleChange('industry', v)}>
                <SelectTrigger className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12" data-testid="profile-industry">
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                  {industries.map(industry => (
                    <SelectItem key={industry} value={industry} className="text-[#0A0F1E] hover:bg-[#F5F8FF]">
                      {industry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-[#0A0F1E]">Location</Label>
              <Input
                value={profile?.location || ''}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="e.g., San Francisco, CA"
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                data-testid="profile-location"
              />
            </div>
          </div>
        </Card>

        {/* Bio */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-[#0A0F1E]">Bio</Label>
            <span className="text-[#5B6472] text-xs">{(profile?.bio || '').length}/200</span>
          </div>
          <Textarea
            value={profile?.bio || ''}
            onChange={(e) => handleChange('bio', e.target.value.slice(0, 200))}
            placeholder="A short bio about yourself..."
            className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[100px]"
            data-testid="profile-bio"
          />
        </Card>

        {/* Skills */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
          <Label className="text-[#0A0F1E] mb-4 block">Skills</Label>
          <form onSubmit={handleAddSkill} className="flex gap-2 mb-4">
            <Input
              value={skillInput}
              onChange={(e) => setSkillInput(e.target.value)}
              placeholder="Type a skill and press Enter"
              className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12 flex-1"
              data-testid="profile-skill-input"
            />
            <Button type="submit" className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl h-12">
              Add
            </Button>
          </form>
          <div className="flex flex-wrap gap-2">
            {profile?.skills?.map((skill, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F5F8FF] text-[#0A0F1E] text-sm border border-[#E8EDF5]"
              >
                {skill}
                <button
                  type="button"
                  onClick={() => handleRemoveSkill(skill)}
                  className="hover:text-[#1B5AFF] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </span>
            ))}
            {(!profile?.skills || profile.skills.length === 0) && (
              <p className="text-[#5B6472] text-sm">No skills added yet.</p>
            )}
          </div>
        </Card>

        {/* Open to Opportunities */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
          <Label className="text-[#0A0F1E] mb-4 block">Open to Opportunities</Label>
          <div className="flex flex-wrap gap-3">
            {[
              { value: 'open', label: 'Open', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
              { value: 'selective', label: 'Selective', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
              { value: 'not_looking', label: 'Not Looking', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleChange('open_to_opportunities', option.value)}
                className={`px-4 py-2 rounded-xl border transition-all ${
                  profile?.open_to_opportunities === option.value
                    ? option.color + ' border-current'
                    : 'bg-[#F5F8FF]/30 text-[#5B6472] border-[#E8EDF5] hover:border-[#5B6472]'
                }`}
                data-testid={`profile-status-${option.value}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl px-8 h-12 btn-glow"
            data-testid="profile-save-btn"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Profile
              </>
            )}
          </Button>
        </div>
      </div>
  );

  return isFounderMode ? profileContent : <DashboardLayout>{profileContent}</DashboardLayout>;
};

export default Profile;
